import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { trackSocialMediaClick, trackNavigation } from '../utils/analytics';

const Footer: React.FC = () => {
  const currentYear = new Date().getFullYear();
  const [showQRCode, setShowQRCode] = useState(false);
  const [showGroupQRCode, setShowGroupQRCode] = useState(false);

  const socialLinks = [
    {
      name: '朝朝AI工坊',
      platform: '小红书',
      url: 'https://www.xiaohongshu.com/user/profile/674c4359000000000800b16a',
      icon: (
        <svg className="w-5 h-5" viewBox="0 0 24 24">
          <circle cx="12" cy="12" r="12" fill="#FF2442"/>
          <path d="M8.5 7h7v2h-7V7zm0 4h7v2h-7v-2zm0 4h5v2h-5v-2z" fill="white"/>
        </svg>
      ),
      color: 'hover:text-red-500'
    },
    {
      name: '朝朝AI工坊',
      platform: '抖音',
      url: 'https://v.douyin.com/CZMfNW5J3ao/',
      icon: (
        <svg className="w-5 h-5" viewBox="0 0 24 24">
          <rect width="24" height="24" rx="6" fill="#000"/>
          <path d="M12.5 6.5c.8 0 1.5-.7 1.5-1.5s-.7-1.5-1.5-1.5-1.5.7-1.5 1.5.7 1.5 1.5 1.5zm-.5 2v8l-2-1.5v-6.5h-1v7l3 2.5 3-2.5v-7h-1v6.5l-2 1.5z" fill="#FF004F"/>
          <circle cx="17" cy="7" r="1.5" fill="#25F4EE"/>
        </svg>
      ),
      color: 'hover:text-gray-800'
    }
  ];

  // 社交媒体链接点击跟踪
  const handleSocialClick = (platform: string, url: string) => {
    trackSocialMediaClick(platform, url);
    window.open(url, '_blank');
  };

  // 页面导航跟踪
  const handleNavClick = (page: string) => {
    trackNavigation('footer', page);
  };

  return (
    <motion.footer 
      className="bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 mt-16 transition-colors duration-200"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex flex-col md:flex-row justify-between items-center space-y-6 md:space-y-0">
          {/* 左侧：项目信息 + 产品链接 */}
          <div className="text-center md:text-left flex-shrink-0">
            <Link to="/plaza" className="flex items-center justify-center md:justify-start space-x-2 mb-2">
              <img src="/images/icons/icon128.png" alt="AI咖 - AI提示词管理平台Logo" className="w-6 h-6 rounded-md object-contain bg-white" />
              <h3 className="text-lg font-semibold text-gray-800 dark:text-white">AI咖</h3>
            </Link>
            <p className="text-sm text-gray-600 dark:text-gray-300 mb-3">
              专业的AI提示词管理系统 - 提升你的AI使用效率
            </p>
            
            {/* 其他产品链接 - 横向排列 */}
            <div className="flex items-center justify-center md:justify-start space-x-4 text-xs text-gray-500 dark:text-gray-400">
              <span className="text-gray-400 dark:text-gray-500">其他产品：</span>
              <a 
                href="https://www.md2image.com"
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-primary transition-colors duration-200 underline hover:no-underline"
                onClick={() => trackNavigation('footer', 'md2image')}
              >
                MD转图片工具
              </a>
              <span>•</span>
              <a 
                href="https://www.consumersim.cc"
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-primary transition-colors duration-200 underline hover:no-underline"
                onClick={() => trackNavigation('footer', 'consumersim')}
              >
                消费者研究
              </a>
            </div>
          </div>

          {/* 右侧：社媒链接和二维码 */}
          <div className="flex flex-col items-center space-y-4 flex-shrink-0">
            <p className="text-sm text-gray-600 dark:text-gray-300 font-medium">关注我们获取更多AI工具资讯</p>
            
            {/* 社媒链接 */}
            <div className="flex items-center space-x-4">
              {socialLinks.map((link) => (
                <motion.button
                  key={`${link.platform}-${link.name}`}
                  onClick={() => handleSocialClick(link.platform, link.url)}
                  className={`group flex items-center space-x-2 px-3 py-2 rounded-lg bg-gray-50 dark:bg-gray-700 hover:bg-gray-100 dark:hover:bg-gray-600 transition-all duration-200 ${link.color}`}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <div className="text-gray-600 group-hover:text-current transition-colors duration-200">
                    {link.icon}
                  </div>
                  <div className="text-sm">
                    <div className="font-medium text-gray-800 dark:text-white">{link.name}</div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">{link.platform}</div>
                  </div>
                </motion.button>
              ))}
            </div>

            {/* 二维码区域 */}
            <div className="flex items-center space-x-6">
              {/* 微信二维码 */}
              <div className="flex flex-col items-center space-y-2">
                <p className="text-xs text-gray-500 dark:text-gray-400">微信</p>
                <motion.div 
                  className="relative group cursor-pointer"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onMouseEnter={() => setShowQRCode(true)}
                  onMouseLeave={() => setShowQRCode(false)}
                >
                  <img 
                    src="/images/social/wechat-qr.png" 
                    alt="微信二维码"
                    className="w-16 h-16 rounded-lg border-2 border-gray-200 group-hover:border-green-400 transition-colors duration-200 object-contain bg-white"
                  />
                  <div className="absolute -bottom-6 left-1/2 transform -translate-x-1/2 text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                    扫码添加微信
                  </div>

                  {/* 悬停显示的大图 */}
                  <AnimatePresence>
                    {showQRCode && (
                      <motion.div
                        className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-4 z-50 max-w-xs sm:max-w-none"
                        initial={{ opacity: 0, scale: 0.8, y: 10 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.8, y: 10 }}
                        transition={{ duration: 0.2 }}
                      >
                        <div className="relative bg-white p-4 rounded-xl shadow-2xl border border-gray-200">
                          {/* 箭头指示器 */}
                          <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-8 border-r-8 border-t-8 border-l-transparent border-r-transparent border-t-white"></div>
                          <div className="absolute top-full left-1/2 transform -translate-x-1/2 translate-y-[-2px] w-0 h-0 border-l-8 border-r-8 border-t-8 border-l-transparent border-r-transparent border-t-gray-200"></div>
                          
                          {/* 大图显示 */}
                          <div className="flex flex-col items-center space-y-3">
                            <div className="w-36 h-36 sm:w-48 sm:h-48 bg-white rounded-lg border border-gray-100 dark:border-gray-600 flex items-center justify-center">
                              <img 
                                src="/images/social/wechat-qr.png" 
                                alt="微信二维码大图"
                                className="max-w-full max-h-full object-contain"
                              />
                            </div>
                            <div className="text-center">
                              <p className="text-sm font-medium text-gray-800 dark:text-white mb-1">扫码添加微信</p>
                              <p className="text-xs text-gray-500 dark:text-gray-400">获取更多AI工具资讯</p>
                            </div>
                          </div>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.div>
              </div>

              {/* 用户交流群二维码 */}
              <div className="flex flex-col items-center space-y-2">
                <p className="text-xs text-gray-500 dark:text-gray-400">用户交流群</p>
                <motion.div 
                  className="relative group cursor-pointer"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onMouseEnter={() => setShowGroupQRCode(true)}
                  onMouseLeave={() => setShowGroupQRCode(false)}
                >
                  <img 
                    src="/images/social/group-qr.png" 
                    alt="用户交流群二维码"
                    className="w-16 h-16 rounded-lg border-2 border-gray-200 group-hover:border-blue-400 transition-colors duration-200 object-contain bg-white"
                  />
                  <div className="absolute -bottom-6 left-1/2 transform -translate-x-1/2 text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                    扫码加入交流群
                  </div>

                  {/* 悬停显示的大图 */}
                  <AnimatePresence>
                    {showGroupQRCode && (
                      <motion.div
                        className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-4 z-50 max-w-xs sm:max-w-none"
                        initial={{ opacity: 0, scale: 0.8, y: 10 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.8, y: 10 }}
                        transition={{ duration: 0.2 }}
                      >
                        <div className="relative bg-white p-4 rounded-xl shadow-2xl border border-gray-200">
                          {/* 箭头指示器 */}
                          <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-8 border-r-8 border-t-8 border-l-transparent border-r-transparent border-t-white"></div>
                          <div className="absolute top-full left-1/2 transform -translate-x-1/2 translate-y-[-2px] w-0 h-0 border-l-8 border-r-8 border-t-8 border-l-transparent border-r-transparent border-t-gray-200"></div>
                          
                          {/* 大图显示 */}
                          <div className="flex flex-col items-center space-y-3">
                            <div className="w-36 h-36 sm:w-48 sm:h-48 bg-white rounded-lg border border-gray-100 flex items-center justify-center">
                              <img 
                                src="/images/social/group-qr.png" 
                                alt="用户交流群二维码大图"
                                className="max-w-full max-h-full object-contain"
                              />
                            </div>
                            <div className="text-center">
                              <p className="text-sm font-medium text-gray-800 dark:text-white mb-1">扫码加入交流群</p>
                              <p className="text-xs text-gray-500 dark:text-gray-400">与其他用户交流AI使用经验</p>
                            </div>
                          </div>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.div>
              </div>
            </div>
          </div>
        </div>

        {/* 底部分割线和版权信息 */}
        <div className="mt-6 pt-6 border-t border-gray-100 dark:border-gray-700">
          <div className="flex flex-col sm:flex-row justify-between items-center space-y-2 sm:space-y-0">
            <p className="text-xs text-gray-500 dark:text-gray-400">
              © {currentYear} AI咖
            </p>
            <div className="flex items-center space-x-4 text-xs text-gray-500 dark:text-gray-400">
              <Link 
                to="/about"
                onClick={() => handleNavClick('about')}
                className="hover:text-gray-700 dark:hover:text-gray-200 transition-colors duration-200 underline"
              >
                关于我们
              </Link>
              <span>•</span>
              <Link 
                to="/privacy"
                onClick={() => handleNavClick('privacy')}
                className="hover:text-gray-700 dark:hover:text-gray-200 transition-colors duration-200 underline"
              >
                隐私政策
              </Link>
              <span>•</span>
              <Link 
                to="/terms"
                onClick={() => handleNavClick('terms')}
                className="hover:text-gray-700 dark:hover:text-gray-200 transition-colors duration-200 underline"
              >
                使用条款
              </Link>
            </div>
          </div>
        </div>
      </div>
    </motion.footer>
  );
};

export default Footer;
